import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const DEFAULT_INPUT = "Hello, 世界! 👨‍💻 Café\u200b";

const MAX_CHARS = 2000;

const CONTROL_NAMES = {
  0x00: "NUL", 0x01: "SOH", 0x02: "STX", 0x03: "ETX", 0x04: "EOT",
  0x05: "ENQ", 0x06: "ACK", 0x07: "BEL", 0x08: "BS",  0x09: "HT",
  0x0a: "LF",  0x0b: "VT",  0x0c: "FF",  0x0d: "CR",  0x0e: "SO",
  0x0f: "SI",  0x10: "DLE", 0x11: "DC1", 0x12: "DC2", 0x13: "DC3",
  0x14: "DC4", 0x15: "NAK", 0x16: "SYN", 0x17: "ETB", 0x18: "CAN",
  0x19: "EM",  0x1a: "SUB", 0x1b: "ESC", 0x1c: "FS",  0x1d: "GS",
  0x1e: "RS",  0x1f: "US",  0x7f: "DEL",
};

const INVISIBLE_NAMES = {
  0x00a0: "NBSP",
  0x200b: "ZWSP",
  0x200c: "ZWNJ",
  0x200d: "ZWJ",
  0x200e: "LRM",
  0x200f: "RLM",
  0x2028: "LSEP",
  0x2029: "PSEP",
  0x202a: "LRE",
  0x202b: "RLE",
  0x202c: "PDF",
  0x202d: "LRO",
  0x202e: "RLO",
  0x2060: "WJ",
  0xfeff: "BOM",
};

const toHexUpper = (n, pad = 4) =>
  n.toString(16).toUpperCase().padStart(pad, "0");

const codepointLabel = (cp) => `U+${toHexUpper(cp, cp > 0xffff ? 5 : 4)}`;

const jsEscape = (cp) =>
  cp <= 0xffff
    ? `\\u${toHexUpper(cp, 4)}`
    : `\\u{${cp.toString(16).toUpperCase()}}`;

const htmlEntityDec = (cp) => `&#${cp};`;
const htmlEntityHex = (cp) => `&#x${toHexUpper(cp, cp > 0xffff ? 5 : 4)};`;

const utf8Bytes = (cp) => {
  const out = [];
  if (cp < 0x80) {
    out.push(cp);
  } else if (cp < 0x800) {
    out.push(0xc0 | (cp >> 6));
    out.push(0x80 | (cp & 0x3f));
  } else if (cp < 0x10000) {
    out.push(0xe0 | (cp >> 12));
    out.push(0x80 | ((cp >> 6) & 0x3f));
    out.push(0x80 | (cp & 0x3f));
  } else {
    out.push(0xf0 | (cp >> 18));
    out.push(0x80 | ((cp >> 12) & 0x3f));
    out.push(0x80 | ((cp >> 6) & 0x3f));
    out.push(0x80 | (cp & 0x3f));
  }
  return out;
};

const utf16Units = (cp) => {
  if (cp <= 0xffff) return [cp];
  const adj = cp - 0x10000;
  return [0xd800 | (adj >> 10), 0xdc00 | (adj & 0x3ff)];
};

const categoryKey = (ch, cp) => {
  if (cp < 0x20 || (cp >= 0x7f && cp < 0xa0)) return "control";
  if (INVISIBLE_NAMES[cp] !== undefined) return "invisible";
  try {
    if (/\p{White_Space}/u.test(ch)) return "whitespace";
    if (/\p{Emoji}/u.test(ch) && !/\p{ASCII}/u.test(ch)) return "emoji";
    if (/\p{L}/u.test(ch)) return "letter";
    if (/\p{N}/u.test(ch)) return "number";
    if (/\p{P}/u.test(ch)) return "punctuation";
    if (/\p{S}/u.test(ch)) return "symbol";
    if (/\p{M}/u.test(ch)) return "mark";
  } catch {
    // Older engines without Unicode property escapes — fall through.
  }
  return "other";
};

const shortName = (cp) => {
  if (CONTROL_NAMES[cp] !== undefined) return CONTROL_NAMES[cp];
  if (INVISIBLE_NAMES[cp] !== undefined) return INVISIBLE_NAMES[cp];
  return null;
};

const renderGlyph = (ch, cp, category) => {
  if (category === "control" || category === "invisible") {
    const label = shortName(cp);
    return label ? `[${label}]` : "·";
  }
  return ch;
};

const analyze = (input) => {
  if (!input) return [];
  const rows = [];
  let idx = 0;
  for (const ch of input) {
    if (rows.length >= MAX_CHARS) break;
    const cp = ch.codePointAt(0);
    const cat = categoryKey(ch, cp);
    rows.push({
      index: idx,
      char: ch,
      codepoint: cp,
      category: cat,
      utf8: utf8Bytes(cp),
      utf16: utf16Units(cp),
      glyph: renderGlyph(ch, cp, cat),
      shortName: shortName(cp),
    });
    idx += ch.length;
  }
  return rows;
};

const summarize = (input, rows) => {
  if (!input) return { units: 0, codepoints: 0, bytes: 0, truncated: false };
  let cpCount = 0;
  let byteCount = 0;
  for (const ch of input) {
    cpCount += 1;
    byteCount += utf8Bytes(ch.codePointAt(0)).length;
  }
  return {
    units: input.length,
    codepoints: cpCount,
    bytes: byteCount,
    truncated: rows.length < cpCount,
  };
};

const writeClipboard = async (text, t) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(t("copy_success"));
  } catch {
    toast.error(t("failed_to_copy"));
  }
};

const UnicodeInspector = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState(DEFAULT_INPUT);

  const rows = useMemo(() => analyze(input), [input]);
  const stats = useMemo(() => summarize(input, rows), [input, rows]);

  const tsv = useMemo(() => {
    if (!rows.length) return "";
    const header = [
      t("unicode_inspector_col_char"),
      t("unicode_inspector_col_codepoint"),
      t("unicode_inspector_col_dec"),
      t("unicode_inspector_col_utf8"),
      t("unicode_inspector_col_js"),
      t("unicode_inspector_col_html"),
      t("unicode_inspector_col_category"),
    ].join("\t");
    const lines = rows.map((r) =>
      [
        r.glyph,
        codepointLabel(r.codepoint),
        String(r.codepoint),
        r.utf8.map((b) => toHexUpper(b, 2)).join(" "),
        jsEscape(r.codepoint),
        htmlEntityDec(r.codepoint),
        t(`unicode_inspector_cat_${r.category}`),
      ].join("\t"),
    );
    return [header, ...lines].join("\n");
  }, [rows, t]);

  const copyTsv = () => {
    if (!tsv) {
      toast.error(t("unicode_inspector_nothing_to_copy"));
      return;
    }
    writeClipboard(tsv, t);
  };

  const copyValue = (value) => {
    if (!value) return;
    writeClipboard(value, t);
  };

  const clear = () => setInput("");
  const loadSample = () => setInput(DEFAULT_INPUT);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("unicode_inspector_title")}
            </h1>
            <p className="text-gray-600">{t("unicode_inspector_intro")}</p>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="unicode-inspector-input"
                  className="text-sm font-medium text-gray-700"
                >
                  {t("unicode_inspector_input_label")}
                </label>
                <div className="flex items-center gap-2">
                  <StandardButton
                    onClick={loadSample}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("unicode_inspector_load_sample")}
                  </StandardButton>
                  <StandardButton
                    onClick={clear}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={!input}
                  >
                    {t("unicode_inspector_clear")}
                  </StandardButton>
                </div>
              </div>
              <textarea
                id="unicode-inspector-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("unicode_inspector_input_placeholder")}
                rows={4}
                aria-label={t("unicode_inspector_input_label")}
                className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none resize-y"
              />
              <div className="text-xs text-gray-500">
                {t("unicode_inspector_stats", {
                  units: stats.units,
                  codepoints: stats.codepoints,
                  bytes: stats.bytes,
                })}
                {stats.truncated && (
                  <span className="ml-2 text-amber-600">
                    {t("unicode_inspector_truncated", { max: MAX_CHARS })}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700">
                {t("unicode_inspector_table_label")}
              </h2>
              <StandardButton
                onClick={copyTsv}
                type="primary"
                size="small"
                disabled={!rows.length}
              >
                {t("unicode_inspector_copy_table")}
              </StandardButton>
            </div>

            {rows.length === 0 ? (
              <div className="text-sm text-gray-400 italic p-4 border border-dashed border-gray-200 rounded-md text-center">
                {t("unicode_inspector_output_empty")}
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-md">
                <table
                  className="min-w-full text-sm"
                  aria-label={t("unicode_inspector_table_label")}
                >
                  <thead className="bg-indigo-50 text-gray-700">
                    <tr>
                      <th scope="col" className="px-2 py-2 text-left font-medium">
                        {t("unicode_inspector_col_char")}
                      </th>
                      <th scope="col" className="px-2 py-2 text-left font-medium">
                        {t("unicode_inspector_col_codepoint")}
                      </th>
                      <th scope="col" className="px-2 py-2 text-left font-medium">
                        {t("unicode_inspector_col_dec")}
                      </th>
                      <th scope="col" className="px-2 py-2 text-left font-medium">
                        {t("unicode_inspector_col_utf8")}
                      </th>
                      <th scope="col" className="px-2 py-2 text-left font-medium">
                        {t("unicode_inspector_col_js")}
                      </th>
                      <th scope="col" className="px-2 py-2 text-left font-medium">
                        {t("unicode_inspector_col_html")}
                      </th>
                      <th scope="col" className="px-2 py-2 text-left font-medium">
                        {t("unicode_inspector_col_category")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={`${r.index}-${r.codepoint}`}
                        className="border-t border-gray-100 hover:bg-indigo-50/40"
                      >
                        <td className="px-2 py-1 font-mono text-base">
                          <span
                            className={
                              r.category === "control" ||
                              r.category === "invisible"
                                ? "text-amber-600"
                                : ""
                            }
                            title={
                              r.shortName
                                ? `${r.shortName} (${codepointLabel(
                                    r.codepoint,
                                  )})`
                                : codepointLabel(r.codepoint)
                            }
                          >
                            {r.glyph}
                          </span>
                        </td>
                        <td className="px-2 py-1 font-mono">
                          <button
                            type="button"
                            onClick={() =>
                              copyValue(codepointLabel(r.codepoint))
                            }
                            className="underline decoration-dotted hover:text-indigo-700"
                            aria-label={t(
                              "unicode_inspector_copy_value_aria",
                              { value: codepointLabel(r.codepoint) },
                            )}
                          >
                            {codepointLabel(r.codepoint)}
                          </button>
                        </td>
                        <td className="px-2 py-1 font-mono">{r.codepoint}</td>
                        <td className="px-2 py-1 font-mono">
                          {r.utf8.map((b) => toHexUpper(b, 2)).join(" ")}
                        </td>
                        <td className="px-2 py-1 font-mono">
                          <button
                            type="button"
                            onClick={() => copyValue(jsEscape(r.codepoint))}
                            className="underline decoration-dotted hover:text-indigo-700"
                            aria-label={t(
                              "unicode_inspector_copy_value_aria",
                              { value: jsEscape(r.codepoint) },
                            )}
                          >
                            {jsEscape(r.codepoint)}
                          </button>
                        </td>
                        <td className="px-2 py-1 font-mono">
                          <button
                            type="button"
                            onClick={() =>
                              copyValue(htmlEntityHex(r.codepoint))
                            }
                            className="underline decoration-dotted hover:text-indigo-700"
                            aria-label={t(
                              "unicode_inspector_copy_value_aria",
                              { value: htmlEntityDec(r.codepoint) },
                            )}
                          >
                            {htmlEntityDec(r.codepoint)}
                          </button>
                        </td>
                        <td className="px-2 py-1 text-gray-600">
                          {t(`unicode_inspector_cat_${r.category}`)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("unicode_inspector_note_codepoints")}</p>
              <p>{t("unicode_inspector_note_invisible")}</p>
              <p>{t("unicode_inspector_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default UnicodeInspector;
